/**
 * Canonical pricing helper — single source of truth for the customer-facing
 * "Starting at …" line rendered on every surface (VendorDetail hero, Browse
 * cards, Plan Event cards, and anywhere else).
 *
 * Rule (strict):
 *   display_price =
 *     if any available packages       → min(package.starting_price)
 *     else if any bookable à-la-carte → min(service.starting_price)
 *     else                            → vendor.starting_price bucket (formatted)
 *
 * "Available" excludes services explicitly toggled off (`available === false`).
 * "Bookable à-la-carte" further excludes clear add-ons ("extra hour",
 * "add-on", "additional …") because those are extras layered on top of a
 * real purchase, not entry points. A standalone service like "Ceremony
 * Sound – $125" IS an entry point and is kept.
 *
 * Non-destructive: this helper never mutates the vendor object. It returns
 * a display string ("Starting at $125", "Request pricing", or the formatted
 * bucket when no services are priced).
 */

const ADDON_NAME_PATTERNS = [
  /\bextra hour\b/i,
  /\badditional hour\b/i,
  /\badditional\s+\d+\s+hour/i,
  /\badd[-\s]?on\b/i,
  /\btravel fee\b/i,
  /\btravel charge\b/i,
  /\brush fee\b/i,
  /\bsetup fee\b/i,
];

export function isLikelyAddOn(service) {
  if (!service || typeof service !== 'object') return false;
  // Explicit opt-out flag wins if set
  if (service.is_addon === true) return true;
  const name = String(service.name || '');
  if (!name) return false;
  return ADDON_NAME_PATTERNS.some((re) => re.test(name));
}

function parseDollarAmount(raw) {
  if (raw == null) return null;
  if (typeof raw === 'number') return Number.isFinite(raw) && raw > 0 ? raw : null;
  const cleaned = String(raw).replace(/[^0-9.]/g, '');
  const n = parseFloat(cleaned);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Internal — returns { amount, source } where source is one of
 * 'offering' | 'bucket' | 'none'. Consumers that only need a
 * number should use `computeStartingPriceAmount`.
 *
 * Rule: the display price is the lowest real standalone offering —
 * packages and standalone à-la-carte services considered TOGETHER (a $45
 * standalone bouquet beats a $250 package for the headline "Starting at").
 * Add-ons (extra hour, travel fee, upgrade-style extras) are excluded from
 * the pool. Only when the pool is empty do we fall back to the stored
 * bucket.
 */
function resolveStartingPrice(vendor) {
  if (!vendor || typeof vendor !== 'object') {
    return { amount: null, source: 'none' };
  }
  const services = Array.isArray(vendor.services) ? vendor.services : [];
  const available = services.filter(
    (s) => s && typeof s === 'object' && s.available !== false
  );

  // Legitimate standalone pool: all packages, plus à-la-carte services that
  // aren't add-ons. Services with no explicit `type` field (older records)
  // are treated as standalone offerings and included unless their name
  // matches an add-on pattern. Add-on-style names ("Extra Hour", "Travel
  // Fee", etc.) are never the entry-point price.
  const offeringPrices = available
    .filter((s) => {
      if (s.type === 'package') return true;
      if (s.type === 'service') return !isLikelyAddOn(s);
      // No type set — treat as standalone unless it's obviously an add-on.
      if (!s.type) return !isLikelyAddOn(s);
      return false;
    })
    .map((s) => parseDollarAmount(s.starting_price))
    .filter(Boolean);

  if (offeringPrices.length > 0) {
    return { amount: Math.min(...offeringPrices), source: 'offering' };
  }

  // Fallback to the vendor-level bucket.
  return { amount: null, source: 'bucket' };
}

/**
 * Raw numeric amount for internal math (e.g. forecast totals). Returns null
 * when only a bucket label is available.
 */
export function computeStartingPriceAmount(vendor) {
  return resolveStartingPrice(vendor).amount;
}

/**
 * Primary API — returns a display string suitable for direct rendering.
 * `formatBucket` is an optional caller-supplied formatter used only when the
 * vendor has no priced services; it receives `vendor.starting_price`.
 */
export function formatVendorStartingPrice(vendor, formatBucket) {
  const { amount, source } = resolveStartingPrice(vendor);
  if (source === 'offering') {
    return `Starting at $${Math.round(amount).toLocaleString()}`;
  }
  // Bucket path — delegate to the caller's existing formatter if provided,
  // otherwise perform a minimal parse so we still render something sensible.
  if (typeof formatBucket === 'function') {
    return formatBucket(vendor?.starting_price);
  }
  const bucket = Array.isArray(vendor?.starting_price)
    ? vendor.starting_price[0]
    : vendor?.starting_price;
  if (typeof bucket !== 'string' || !bucket) return 'Request pricing';
  const range = bucket.match(/\$?([\d,]+)\s*[-–]\s*\$?([\d,]+)/);
  if (range) return `Starting at $${range[1]}`;
  const under = bucket.match(/^Under\s*\$?([\d,]+)/i);
  if (under) return `Starting at $${under[1]}`;
  const num = bucket.match(/^\$?([\d,]+)$/);
  if (num) return `Starting at $${num[1]}`;
  return bucket.startsWith('$') ? `Starting at ${bucket}` : bucket;
}
